import {
  LocalBlockTypeType,
  ServerBlockTypeType,
  getBlockType
} from "@pathwright/blocks-core"
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef
} from "react"
import {
  BlocksConfigContextType,
  useBlocksConfig
} from "../config/BlocksConfigProvider"
import {
  SyncedReducerReturnType,
  useSyncedReducer
} from "../syncer/useSyncedReducer"
import { BlockType, BlocksContentType, BlocksContextType } from "../types"
import * as actionTypes from "./constants"
import {
  ViewerReducerActionType,
  ViewerStateType,
  defaultState,
  getInitialState,
  reducer
} from "./viewerState"

export type SaveViewBlockType = (
  blockId: string,
  change: Record<string, string>,
  trackProgress?: boolean
) => void

export type ViewerContextValueType = ViewerStateType & {
  saveBlock: SaveViewBlockType

  // ContentViewerContainer props
  content?: BlocksContentType
  blockTypes?: ServerBlockTypeType[]
  blocksContext?: BlocksContextType
  contentLoading?: boolean
}

export type ViewerStateProviderPropsType = {
  children: ReactNode | ((props: ViewerContextValueType) => ReactNode)
  draft?: boolean // This allows student data to be saved on draft blocks
  syncer: (
    action: ViewerReducerActionType,
    state: ViewerStateType,
    dispatch: (action: ViewerReducerActionType) => void
  ) => void
  content: BlocksContentType
  blockTypes: ServerBlockTypeType[]
  blocksContext: BlocksContextType
  contentLoading?: boolean
  needsContentReset?: boolean
}

const intitialContextValue: ViewerContextValueType = {
  ...defaultState,
  saveBlock: () => {}
}

type jwtResult = {
  userID?: string
}

const ViewerStateContext =
  createContext<ViewerContextValueType>(intitialContextValue)

export const useViewerState = (): ViewerContextValueType =>
  useContext(ViewerStateContext)

const ViewerStateProvider = ({
  children,
  draft = false,
  ...props
}: ViewerStateProviderPropsType) => {
  const {
    // From Syncer
    syncer,
    content,
    blockTypes,
    blocksContext,
    contentLoading,
    needsContentReset
  } = props

  const { onProgress, userID, isPreviewMode }: BlocksConfigContextType =
    useBlocksConfig()

  const [state, dispatch]: SyncedReducerReturnType<
    ViewerStateType,
    ViewerReducerActionType
  > = useSyncedReducer<
    ViewerStateType,
    ViewerReducerActionType,
    { content: BlocksContentType }
  >(reducer, syncer, { content }, getInitialState)

  const { blocks, completion, error, syncing, lastModified }: ViewerStateType =
    state

  // Debug the Viewer state
  window.logContentState = useCallback(() => {
    const nextState: ViewerStateType = reducer(state, {
      type: actionTypes.GET_STATE
    })
    console.log("Viewer state: ", JSON.stringify(nextState, null, 2))
  }, [state])

  const contentRef = useRef(content)
  contentRef.current = content

  useEffect(() => {
    if (needsContentReset) {
      dispatch({
        type: actionTypes.RESET,
        payload: {
          // Prefer ref so RESET sees content from this commit when the pulse
          // lands in the same turn as a content update (e.g. assessment step
          // reset soft-clearing answers).
          content: contentRef.current,
          blockTypes
        }
      })
    }
  }, [needsContentReset, blockTypes])

  useEffect(() => {
    if (onProgress) onProgress(completion)
  }, [completion])

  const handleSaveBlock: SaveViewBlockType = useCallback(
    async (id, data, trackProgress): Promise<void | undefined> => {
      const block: BlockType | undefined = blocks.find(
        (block) => block.id === id
      )

      if ((!userID && !isPreviewMode) || !block) {
        console.error(
          "Cannot save blocks data without data and an authenticated user."
        )
        return
        // TODO: This should be an error, but Jest can't handle it
        // throw new Error("Cannot save blocks data without an authenticated user.")
      }

      const blockType: LocalBlockTypeType | undefined = getBlockType(block.type)

      let completion = { ...(block.completion || {}) }

      // Optimistic completion update
      if (typeof blockType?.resolveUserCompletion === "function") {
        completion = await blockType.resolveUserCompletion(
          null,
          data,
          block.layout
        )
      }

      dispatch({
        type: actionTypes.UPDATE_USER_BLOCK,
        payload: {
          id,
          data,
          trackProgress,
          userID: userID || 123456789,
          draft,
          completion
        }
      })
    },
    [content, blocks, userID, draft, dispatch]
  )

  const provideState: ViewerContextValueType = {
    // Local state
    blocks,
    completion,
    error,
    syncing,
    lastModified,

    // Handlers
    saveBlock: handleSaveBlock,

    // Passed syncer state
    content,
    blockTypes,
    blocksContext,
    contentLoading
  }

  const renderChildren =
    typeof children === "function"
      ? (val: ViewerContextValueType) => children(val)
      : () => children

  return (
    <ViewerStateContext.Provider value={provideState}>
      {renderChildren(provideState)}
    </ViewerStateContext.Provider>
  )
}

export default ViewerStateProvider
